/**
 * MobileTabBar — bottom navigation. Five thumb-reach destinations.
 * Active state uses the v1 convention: primary-50 tint + primary-700 text.
 * Never inverted / never a dark background (per design system invariants).
 *
 * Accepts optional `items` to swap in a different nav set (e.g. Planning app).
 * When `items` is omitted it uses the default prioritization TAB_ITEMS.
 */
import { useLocation, useNavigate } from 'react-router-dom';
import { TAB_ITEMS, activeTabHref, MORE_SENTINEL, SEARCH_SENTINEL, type MobileNavItem } from './navConfig';

interface MobileTabBarProps {
  onMore: () => void;
  onSearch?: () => void;
  moreOpen: boolean;
  searchOpen?: boolean;
  /** Override the default tab set (used by the Planning shell). */
  items?: MobileNavItem[];
  /** Override the active-tab resolver when using custom items. */
  activeHref?: string | null;
  /** Open a tab's submenu sheet (Priorities, Plans, Engine, etc.). */
  onTabSubmenu?: (tab: MobileNavItem) => void;
}

export function MobileTabBar({
  onMore,
  onSearch,
  moreOpen,
  searchOpen = false,
  items,
  activeHref,
  onTabSubmenu,
}: MobileTabBarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const tabs = items ?? TAB_ITEMS;
  const active =
    activeHref !== undefined
      ? activeHref
      : moreOpen || searchOpen
        ? null
        : activeTabHref(location.pathname);

  const handle = (tab: MobileNavItem) => {
    if (tab.submenu?.length) {
      onTabSubmenu?.(tab);
      return;
    }
    const href = tab.href;
    if (href === MORE_SENTINEL) return onMore();
    if (href === SEARCH_SENTINEL) return onSearch?.();
    navigate(href);
  };

  return (
    <nav className="pa-pb-safe fixed bottom-0 inset-x-0 z-40 bg-white/95 dark:bg-[color-mix(in_srgb,var(--bg-raised)_95%,transparent)] backdrop-blur-md border-t border-gray-200 dark:border-[var(--rule)]">
      <ul className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length}, 1fr)` }}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive =
            tab.href === MORE_SENTINEL ? moreOpen :
            tab.href === SEARCH_SENTINEL ? searchOpen :
            active === tab.href;
          return (
            <li key={tab.href + tab.label}>
              <button
                type="button"
                onClick={() => handle(tab)}
                aria-label={tab.label}
                aria-current={isActive ? 'page' : undefined}
                className="w-full h-16 flex flex-col items-center justify-center gap-1 select-none"
              >
                <span
                  className={`flex items-center justify-center h-8 w-[3.25rem] rounded-full transition-all ${
                    isActive
                      ? 'bg-primary-100 text-primary-800 shadow-sm ring-2 ring-primary-300/60 dark:bg-primary-500/35 dark:text-primary-200 dark:ring-primary-400/50'
                      : 'text-gray-500 dark:text-[var(--text-ter)]'
                  }`}
                >
                  <Icon className={`h-[22px] w-[22px] ${isActive ? 'stroke-[2.5]' : 'stroke-[2]'}`} />
                </span>
                <span
                  className={`text-[11px] leading-none ${
                    isActive
                      ? 'text-primary-800 dark:text-primary-200 font-bold'
                      : 'text-gray-500 dark:text-[var(--text-ter)] font-medium'
                  }`}
                >
                  {tab.label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
